/**
 * The chips under a dictation prompt's instructions. Each chip stands for one
 * sentence-group on its own line of the prompt text: switching a chip on adds
 * its line, switching it off removes it, and a chip shows as on while its
 * exact line is in the text — an edited line no longer counts. Translate's
 * line is always kept last: every other instruction then works on the
 * original language, which the speaker can read.
 */

export type PromptChipId =
  | "clean-up"
  | "make-concise"
  | "translate"
  | "bullet-points"
  | "formal";

type PromptChip = { id: PromptChipId };

export const PROMPT_CHIPS: readonly PromptChip[] = [
  { id: "clean-up" },
  { id: "make-concise" },
  { id: "translate" },
  { id: "bullet-points" },
  { id: "formal" },
];

/** The languages Translate offers. */
export const TRANSLATE_LANGUAGES: readonly string[] = [
  "English",
  "German",
  "Spanish",
  "French",
  "Chinese",
  "Japanese",
];

export const CLEAN_UP_SENTENCE =
  "Fix punctuation, capitalization, and obvious speech-recognition mistakes. Remove filler words (um, uh, you know, like), stutters, and false starts. When the speaker corrects themselves, keep only the corrected version. Start a new paragraph where the topic changes. Do not rephrase, shorten, or change word choice beyond that.";

export const MAKE_CONCISE_SENTENCE =
  "Rewrite the text so it is clear and brief. Remove filler, repetition, and hedging. Keep every fact, decision, name, number, and request. Keep the speaker's tone and first-person voice. Do not add a greeting or sign-off that was not spoken.";

const FIXED_SENTENCES: Record<Exclude<PromptChipId, "translate">, string> = {
  "clean-up": CLEAN_UP_SENTENCE,
  "make-concise": MAKE_CONCISE_SENTENCE,
  "bullet-points":
    "Format the text as a short bulleted list, one point per line.",
  formal: "Use a polite, professional tone.",
};

const TRANSLATE_PREFIX = "Translate the text into natural, fluent ";
const TRANSLATE_SUFFIX =
  ". Keep names, numbers, product names, and code terms unchanged.";

/** Translate's line for one language. */
export function translateSentence(language: string): string {
  return `${TRANSLATE_PREFIX}${language}${TRANSLATE_SUFFIX}`;
}

/** The language of a Translate line, or null for any other line. */
function translateLanguageOf(line: string): string | null {
  const trimmed = line.trim();
  if (
    !trimmed.startsWith(TRANSLATE_PREFIX) ||
    !trimmed.endsWith(TRANSLATE_SUFFIX)
  )
    return null;
  const language = trimmed
    .slice(TRANSLATE_PREFIX.length, trimmed.length - TRANSLATE_SUFFIX.length)
    .trim();
  return language || null;
}

/** The non-blank lines of a prompt, as written. */
function promptLines(text: string): string[] {
  return text.split("\n").filter((line) => line.trim().length > 0);
}

function hasLine(lines: string[], sentence: string): boolean {
  return lines.some((line) => line.trim() === sentence);
}

/** The language Translate writes in this prompt, or null when it is off. */
export function translateLanguageIn(text: string): string | null {
  for (const line of promptLines(text)) {
    const language = translateLanguageOf(line);
    if (language) return language;
  }
  return null;
}

/** The chips whose line is in the text, in chip order. */
export function chipsIn(text: string): PromptChipId[] {
  const lines = promptLines(text);
  return PROMPT_CHIPS.filter(({ id }) =>
    id === "translate"
      ? lines.some((line) => translateLanguageOf(line) !== null)
      : hasLine(lines, FIXED_SENTENCES[id]),
  ).map(({ id }) => id);
}

/**
 * The text with one chip switched. Translate takes the language to write in,
 * replacing any earlier one, or null to remove it; every other chip flips.
 * Blank lines are dropped.
 */
export function toggleChip(
  text: string,
  id: PromptChipId,
  language: string | null = null,
): string {
  const lines = promptLines(text);
  const isTranslate = (line: string) => translateLanguageOf(line) !== null;
  const rest = lines.filter((line) => !isTranslate(line));
  if (id === "translate") {
    return (language ? [...rest, translateSentence(language)] : rest).join(
      "\n",
    );
  }
  const sentence = FIXED_SENTENCES[id];
  if (hasLine(lines, sentence)) {
    return lines.filter((line) => line.trim() !== sentence).join("\n");
  }
  return [...rest, sentence, ...lines.filter(isTranslate)].join("\n");
}
